import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

export const scrollToHash = (
  hash: string,
  scrollArg: ScrollIntoViewOptions = { behavior: 'smooth', block: 'center' }
) => {
  const element = document.querySelector(hash);
  if (element) {
    element.scrollIntoView(scrollArg);
  }
};

/**
 * Custom React hook that resets the scroll position to the top of the page or to a hash anchor
 * after a route change, unless the user has already started scrolling.
 *
 * - If a hash is present in the URL, it scrolls to the corresponding element after a delay.
 * - If no hash is present and the user has not scrolled, it smoothly scrolls to the top.
 * - If the user has scrolled before the delay, it does not reset the scroll position.
 *
 * @param delay - The time in milliseconds to wait before performing the scroll reset. Defaults to 1500ms.
 *
 * @remarks
 * This hook is useful for single-page applications where you want to ensure the user starts at the top
 * of a new route, but you don't want to disrupt users who have already begun interacting with the page.
 *
 * @example
 * ```tsx
 * useScrollReset(); // Uses default delay of 1500ms
 * useScrollReset(1000); // Uses a custom delay
 * ```
 */
export const useScrollReset = (delay: number = 1500) => {
  const pathname = usePathname();
  const hasScrolledRef = useRef(false);
  const rafIdRef = useRef<number | null>(null);
  const timeoutIdRef = useRef<number | null>(null);

  useEffect(() => {
    // Disable browser automatic scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const onScroll = () => {
      hasScrolledRef.current = true;
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    const hash = window.location.hash;
    timeoutIdRef.current = window.setTimeout(() => {
      rafIdRef.current = window.requestAnimationFrame(() => {
        if (hash) {
          scrollToHash(hash);
        } else if (!hasScrolledRef.current) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    }, delay);

    return () => {
      if (timeoutIdRef.current !== null) {
        clearTimeout(timeoutIdRef.current);
      }
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      window.removeEventListener('scroll', onScroll);
      hasScrolledRef.current = false;
    };
  }, [pathname, delay]);
};
